import {
  StyleSheet,
  Dimensions,
  Text,
  View,
  TouchableOpacity,
  Linking,
  Platform,
  Modal,
  ScrollView,
} from "react-native";
import { BarCodeScanner } from "expo-barcode-scanner";
import { Camera } from 'expo-camera';
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import PageTitle from "./PageTitle";
import { useIsFocused, useFocusEffect } from "@react-navigation/native";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import IconButton from "./ButtonIcon";
import * as Colors from "../styles/colors";
import * as Fonts from "../styles/typography";
import { Toast } from "react-native-toast-message/lib/src/Toast";
import { AuthContext } from "../contexts/auth";
import TicketKeyboard from "./TicketKeyboard";
import { IS_WEB } from "@services/Config";

// Import ZXing for web QR/barcode detection
let BrowserMultiFormatReader;
if (IS_WEB) {
  try {
    const ZXing = require('@zxing/browser');
    BrowserMultiFormatReader = ZXing.BrowserMultiFormatReader;
  } catch (error) {
    console.warn('ZXing not available for web QR/barcode scanning:', error);
  }
}

const { width, height } = Dimensions.get("screen");

export default function ScanQRCode({ navigation }) {
  const {
    roleUser
  } = useContext(AuthContext)
  const [hasPermission, setHasPermission] = useState(null);
  const [type, setType] = useState(BarCodeScanner.Constants.Type.back);
  const [scanned, setScanned] = useState(false);
  const [visible, setVisible] = useState(false);
  const isFocused = useIsFocused();
  
  // Web-specific state
  const [webError, setWebError] = useState(null);
  const [webData, setWebData] = useState(null);
  const [isWebScannerActive, setIsWebScannerActive] = useState(false);
  const [scanningStatus, setScanningStatus] = useState('initializing'); // 'initializing', 'scanning', 'error', 'success'
  const [detectedCodeType, setDetectedCodeType] = useState(null);
  
  // Camera selection state
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [showCameraSelector, setShowCameraSelector] = useState(false);
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  
  // Responsive dimensions state
  const [windowDimensions, setWindowDimensions] = useState({
    width: width,
    height: height
  });
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scannerRef = useRef(null);
  const isWebScanActiveRef = useRef(true);
  const cleanupTimeoutRef = useRef(null);
  const navigationListenerRef = useRef(null);
  const scannerSessionId = useRef(0);
  const currentCameraIdRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  // Handle window resize for responsive video sizing
  const handleResize = useCallback(() => {
    if (IS_WEB) {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      setWindowDimensions({
        width: newWidth,
        height: newHeight
      });
      
      // Update video element dimensions if it exists
      if (videoRef.current) {
        const videoElement = videoRef.current;
        const { width: containerWidth, height: containerHeight } = getOptimalVideoDimensions(newWidth, newHeight);
        
        // Update video element style
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'contain';
        
        console.log("Window resized, updated video dimensions:", {
          windowWidth: newWidth,
          windowHeight: newHeight,
          containerWidth,
          containerHeight
        });
      }
    }
  }, [getOptimalVideoDimensions]);

  // Debounced resize handler
  const handleResizeDebounced = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(handleResize, 100);
  }, [handleResize]);

  // Calculate optimal video dimensions
  const getOptimalVideoDimensions = useCallback((windowWidth, windowHeight) => {
    // Target aspect ratio (more realistic for modern cameras)
    const targetAspectRatio = 16 / 9; // 16:9 aspect ratio (more common for modern cameras)
    
    // Available space (with some padding)
    const maxWidth = Math.min(windowWidth * 0.8, 800);
    const maxHeight = Math.min(windowHeight * 0.7, 500); // Increased height usage
    
    // Calculate dimensions that fit within available space
    let width = maxWidth;
    let height = width / targetAspectRatio;
    
    // If height exceeds available space, scale down
    if (height > maxHeight) {
      height = maxHeight;
      width = height * targetAspectRatio;
    }
    
    // Ensure minimum dimensions
    width = Math.max(width, 300);
    height = Math.max(height, 150); // Reduced minimum height
    
    return { width, height };
  }, []);

  // Function to get available cameras
  const getAvailableCameras = async () => {
    if (!IS_WEB || !BrowserMultiFormatReader) return [];
    
    try {
      const videoInputDevices = await BrowserMultiFormatReader.listVideoInputDevices();
      console.log("Available video devices (raw):", videoInputDevices);
      // Filter out devices with undefined deviceId
      const cameras = videoInputDevices
        .filter(device => !!device.deviceId)
        .map((device, index) => ({
          id: device.deviceId,
          label: device.label || `Câmera ${index + 1}`,
          index: index,
          isBackCamera: device.label ? device.label.toLowerCase().includes('back') : false
        }));
      console.log("Available video devices (filtered):", cameras);
      setAvailableCameras(cameras);
      
      // Auto-select camera with priority for back camera
      if (cameras.length > 0 && !selectedCameraId) {
        // First, try to find a back camera
        const backCamera = cameras.find(camera => camera.isBackCamera);
        const cameraToSelect = backCamera || cameras[0];
        
        console.log("Auto-selecting camera:", {
          selected: cameraToSelect.id,
          label: cameraToSelect.label,
          isBackCamera: cameraToSelect.isBackCamera,
          totalCameras: cameras.length
        });
        
        setSelectedCameraId(cameraToSelect.id);
        currentCameraIdRef.current = cameraToSelect.id;
      } else if (cameras.length === 0) {
        console.warn("No valid cameras found (all deviceId undefined). Try granting permission or refreshing device list.");
      }
      return cameras;
    } catch (error) {
      console.error('Error getting available cameras:', error);
      return [];
    }
  };

  // Function to switch camera with proper cleanup
  const switchCamera = async (newCameraId) => {
    console.log("Switching to camera:", newCameraId);
    console.log("Current scanner state:", { isWebScannerActive, scanningStatus, selectedCameraId });
    
    // Set switching flag to prevent interference
    setIsSwitchingCamera(true);
    
    // Set the new camera ID
    setSelectedCameraId(newCameraId);
    setShowCameraSelector(false);
    
    // Update the current camera ref
    currentCameraIdRef.current = newCameraId;
    
    // Force stop current scanner completely with longer cleanup time
    console.log("Force stopping current scanner...");
    forceStopScanner();
    
    // Wait for cleanup to complete with extended timeout
    console.log("Waiting for cleanup to complete...");
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Additional cleanup to ensure all resources are released
    if (videoRef.current && videoRef.current.srcObject) {
      try {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => {
          track.stop();
          track.enabled = false;
        });
        videoRef.current.srcObject = null;
      } catch (error) {
        console.error('Error cleaning up video tracks during switch:', error);
      }
    }
    
    // Wait a bit more to ensure complete cleanup
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Reinitialize scanner with new camera
    if (isFocused && hasPermission && !scanned) {
      console.log("Reinitializing scanner with new camera:", newCameraId);
      try {
        await initializeWebScanner();
      } catch (error) {
        console.error('Error reinitializing scanner after camera switch:', error);
        setWebError(new Error('Erro ao trocar de câmera. Tente novamente.'));
        setScanningStatus('error');
      }
    } else {
      console.log("Cannot reinitialize scanner:", { isFocused, hasPermission, scanned });
    }
    
    // Clear switching flag after a longer delay to allow initialization to complete
    setTimeout(() => {
      setIsSwitchingCamera(false);
    }, 1500);
  };

  // Camera selection modal component
  const CameraSelectorModal = () => (
    <Modal
      animationType="fade"
      transparent={true}
      style={{ backgroundColor: "#000" }}
      visible={showCameraSelector}
      onRequestClose={() => setShowCameraSelector(false)}
    >
      <View style={styles.centeredView}>
        <View style={styles.modalView}>
          <Icon
            name="close"
            size={32}
            onPress={() => setShowCameraSelector(false)}
            style={styles.closeButton}
          />
          <View style={styles.titleView}>
            <Text style={styles.titleText}>{"Selecionar Câmera"}</Text>
          </View>
          <ScrollView 
            contentContainerStyle={styles.cameraListContainer}
            style={{ width: "100%" }}
          >
            {availableCameras.length === 0 ? (
              <Text style={styles.noCamerasText}>
                Nenhuma câmera disponível
              </Text>
            ) : (
              availableCameras.map((camera) => (
                <TouchableOpacity
                  key={camera.id}
                  style={[
                    styles.cameraOption,
                    selectedCameraId === camera.id && styles.selectedCameraOption
                  ]}
                  onPress={() => {
                    switchCamera(camera.id);
                  }}
                >
                  <Icon 
                    name="camera" 
                    size={24} 
                    color={selectedCameraId === camera.id ? Colors.WHITE : Colors.PRIMARY} 
                  />
                  <Text style={[
                    styles.cameraOptionText,
                    selectedCameraId === camera.id && styles.selectedCameraOptionText
                  ]}>
                    {camera.label} {camera.isBackCamera && "(Traseira)"}
                  </Text>
                  {selectedCameraId === camera.id && (
                    <Icon name="check" size={20} color={Colors.WHITE} />
                  )}
                </TouchableOpacity>
              ))
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // Add navigation listener to reset state when returning to this screen
  useEffect(() => {
    navigationListenerRef.current = navigation.addListener('focus', () => {
      console.log("Navigation focus event - resetting scanner state");
      // Force reset all scanner state
      setScanned(false);
      setWebError(null);
      setScanningStatus('initializing');
      setDetectedCodeType(null);
      setWebData(null);
      
      // Clear any pending timeouts
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      
      // Force stop any existing scanner completely
      if (IS_WEB) {
        forceStopScanner();
      }
    });

    return () => {
      if (navigationListenerRef.current) {
        navigationListenerRef.current();
      }
    };
  }, [navigation]);

  // Reset scanner state when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      console.log("Screen focused, resetting scanner state");
      
      // Immediately clear all scanner-related state
      setScanned(false);
      setWebError(null);
      setScanningStatus('initializing');
      setDetectedCodeType(null);
      setWebData(null);
      
      // Clear any pending cleanup timeouts
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      
      // Force stop any existing scanner immediately
      if (IS_WEB) {
        forceStopScanner();
        // Get available cameras when screen focuses
        getAvailableCameras();
      }
      
      return () => {
        // Cleanup when screen loses focus
        console.log("Screen losing focus, cleaning up scanner");
        if (IS_WEB) {
          forceStopScanner();
        }
      };
    }, [])
  );

  // Robust useEffect for web scanner initialization with proper refs checking
  useEffect(() => {
    if (IS_WEB && hasPermission && isFocused && !scanned && !isSwitchingCamera) {
      console.log("Screen focused, checking refs availability");
      
      // Stop any existing scanner first
      if (scannerRef.current) {
        stopWebScanner();
      }
      
      // Add a small delay to ensure state is fully reset before initializing
      const delayedInitialization = () => {
        // Wait for refs to be available before initializing
        const checkRefsAndInitialize = () => {
          if (videoRef.current && canvasRef.current) {
            console.log("Refs are available, initializing web scanner");
            initializeWebScanner();
          } else {
            console.log("Refs not yet available, retrying in 50ms");
            cleanupTimeoutRef.current = setTimeout(checkRefsAndInitialize, 50);
          }
        };
        
        // Start checking for refs availability
        checkRefsAndInitialize();
      };
      
      // Small delay to ensure state reset is complete
      cleanupTimeoutRef.current = setTimeout(delayedInitialization, 200);
    }
    
    // Cleanup when component unmounts or dependencies change
    return () => {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      if (IS_WEB) {
        console.log("Cleaning up camera resources");
        stopWebScanner();
      }
    };
  }, [hasPermission, isFocused, scanned, isSwitchingCamera]);

  // Keep the original useEffect for initial permission request
  useEffect(() => {
    (async () => {
      try {
        if (IS_WEB) {
          // For web, we need to request camera permission first
          try {
            // Request camera permission
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            // Stop the test stream immediately
            stream.getTracks().forEach(track => track.stop());
            
            // Permission granted, set permission state
            setHasPermission(true);
            
            // Immediately get available cameras and auto-select first one
            console.log("Camera permission granted via button, loading available cameras...");
            await getAvailableCameras();
          } catch (permError) {
            console.error("Camera permission denied:", permError);
            setHasPermission(false);
          }
        } else {
          const { status } = await BarCodeScanner.requestPermissionsAsync();
          setHasPermission(status === "granted");
        }
      } catch (error) {
        console.error("Error requesting camera permission:", error);
        setHasPermission(false);
      }
    })();
  }, []);

  // Handle web barcode scanning - with additional safety checks
  useEffect(() => {
    // Only handle navigation after a successful scan
    if (IS_WEB && webData && scanned) {
      // Ensure webData is a string
      const qrString = typeof webData === 'string' ? webData : String(webData);
      console.log("Navigating to PaymentTicket with QR:", qrString);
      navigation.navigate("PaymentTicket", qrString);
      // Stop the scanner after navigation
      setTimeout(() => {
        forceStopScanner();
        setWebData(null);
      }, 200);
    } else if (IS_WEB && webData && (!isFocused || !isWebScannerActive)) {
      console.log("Ignoring stale web data:", { webData, scanned, isFocused, isWebScannerActive });
      setWebData(null); // Clear stale data
    }
  }, [webData, scanned, isFocused, isWebScannerActive]);

  const stopWebScanner = () => {
    isWebScanActiveRef.current = false;
    console.log("Stopping web scanner");
    
    // Clear any pending timeouts
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
    
    if (scannerRef.current) {
      try {
        // For ZXing, we need to properly reset and stop the scanner
        if (typeof scannerRef.current.reset === 'function') {
          scannerRef.current.reset();
        }
        if (typeof scannerRef.current.stopAsyncDecode === 'function') {
          scannerRef.current.stopAsyncDecode();
        }
        // Force destroy the scanner instance
        scannerRef.current = null;
      } catch (error) {
        console.error('Error stopping scanner:', error);
      }
    }
    
    if (videoRef.current && videoRef.current.srcObject) {
      try {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => {
          track.stop();
          if (videoRef.current.srcObject && videoRef.current.srcObject.removeTrack) {
            videoRef.current.srcObject.removeTrack(track);
          }
        });
        videoRef.current.srcObject = null;
      } catch (error) {
        console.error('Error stopping camera tracks:', error);
      }
    }
    
    setIsWebScannerActive(false);
    setScanningStatus('initializing');
    console.log("Web scanner stopped");
  };

  const forceStopScanner = () => {
    console.log("Force stopping scanner");
    isWebScanActiveRef.current = false;
    // Increment session ID to invalidate all previous callbacks
    scannerSessionId.current += 1;
    
    // Clear all timeouts
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
    
    // Aggressively stop scanner and codeReader
    if (scannerRef.current) {
      try {
        console.log("Stopping codeReader instance");
        if (typeof scannerRef.current.reset === 'function') {
          console.log("Calling codeReader.reset()");
          scannerRef.current.reset();
        }
        if (typeof scannerRef.current.stopAsyncDecode === 'function') {
          console.log("Calling codeReader.stopAsyncDecode()");
          scannerRef.current.stopAsyncDecode();
        }
        if (typeof scannerRef.current.stopStreams === 'function') {
          console.log("Calling codeReader.stopStreams()");
          scannerRef.current.stopStreams();
        }
        if (typeof scannerRef.current.stop === 'function') {
          console.log("Calling codeReader.stop()");
          scannerRef.current.stop();
        }
        console.log("Setting scannerRef.current to null");
        scannerRef.current = null;
      } catch (error) {
        console.error('Error force stopping scanner:', error);
      }
    }
    
    // More aggressive video track cleanup
    if (videoRef.current && videoRef.current.srcObject) {
      try {
        console.log("Stopping video tracks");
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => {
          try {
            track.stop();
            track.enabled = false;
            // Force release the track
            if (track.readyState === 'live') {
              track.stop();
            }
          } catch (trackError) {
            console.error('Error stopping individual track:', trackError);
          }
        });
        videoRef.current.srcObject = null;
      } catch (error) {
        console.error('Error force stopping video tracks:', error);
      }
    }
    
    // Additional cleanup for any remaining media streams
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        // This is a fallback to ensure all streams are properly closed
        const streams = navigator.mediaDevices.getUserMedia({ video: true });
        if (streams && streams.then) {
          streams.then(stream => {
            if (stream) {
              stream.getTracks().forEach(track => track.stop());
            }
          }).catch(() => {
            // Ignore errors in this cleanup step
          });
        }
      } catch (error) {
        // Ignore errors in this cleanup step
      }
    }
    
    setIsWebScannerActive(false);
    setScanningStatus('initializing');
    setWebData(null);
    setDetectedCodeType(null);
    setWebError(null);
    currentCameraIdRef.current = null;
    console.log("Scanner force stopped");
  };

  const initializeWebScanner = async () => {
    try {
      forceStopScanner();
      await new Promise(resolve => setTimeout(resolve, 300));
      isWebScanActiveRef.current = true;
      // New session for this scanner instance
      const thisSessionId = ++scannerSessionId.current;
      console.log("Initializing web scanner, sessionId:", thisSessionId);
      setScanningStatus('initializing');
      
      // Test camera permission first
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop());
      } catch (permError) {
        setWebError(new Error('Permissão da câmera negada. Por favor, permita o acesso à câmera no navegador.'));
        setIsWebScannerActive(false);
        setScanningStatus('error');
        return;
      }
      
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
      
      if (BrowserMultiFormatReader) {
        console.log("Using ZXing BrowserMultiFormatReader for web scanning");
        let codeReader = null;
        try {
          codeReader = new BrowserMultiFormatReader();
          let auxavailableCameras = availableCameras;

          // Get available cameras if not already loaded
          if (availableCameras.length === 0) {
            console.log("No cameras loaded, getting available cameras...");
            const cameras = await getAvailableCameras();
            if (cameras.length === 0) {
              setWebError(new Error('Nenhuma câmera detectada. Verifique se há câmeras conectadas e tente novamente.'));
              setIsWebScannerActive(false);
              setScanningStatus('error');
              return;
            }

            if (auxavailableCameras.length === 0){
              auxavailableCameras = cameras;
            }
          }
          
          
          
          // Ensure we have a camera selected - prioritize selectedCameraId, then first available camera
          let deviceIdToUse = selectedCameraId;
          if (!deviceIdToUse && auxavailableCameras.length > 0) {
            console.log("No camera selected, auto-selecting first available camera");
            deviceIdToUse = auxavailableCameras[0].id;
            setSelectedCameraId(deviceIdToUse);
            currentCameraIdRef.current = deviceIdToUse;
          }
          
          console.log("Camera selection details:", {
            selectedCameraId,
            availableCameras: auxavailableCameras.map(c => ({ id: c.id, label: c.label })),
            deviceIdToUse,
            isSwitchingCamera,
            currentCameraIdRef: currentCameraIdRef.current
          });
          
          // If we're switching cameras and this isn't the camera we want, skip initialization
          if (isSwitchingCamera && currentCameraIdRef.current !== deviceIdToUse) {
            console.log("Skipping initialization - camera switching in progress");
            return;
          }
          
          // Update the current camera ref
          currentCameraIdRef.current = deviceIdToUse;
          
          if (!deviceIdToUse) {
            setWebError(new Error('Nenhuma câmera selecionada. Por favor, selecione uma câmera.'));
            setIsWebScannerActive(false);
            setScanningStatus('error');
            return;
          }
          
          console.log("Using camera device ID:", deviceIdToUse);
          
          // Session-guarded callback
          const scanCallback = (result, error) => {
            // Guard: Ignore all callbacks if scanner is not active or already scanned
            if (scannerSessionId.current !== thisSessionId || !isWebScanActiveRef.current || scanned) {
              return;
            }
            if (result) {
              console.log("Code detected with ZXing:", { text: result.text, format: result.format });
              if (isWebScanActiveRef.current && !scanned) {
                setWebData(result.text);
                setScanningStatus('success');
                setDetectedCodeType(result.format);
                setScanned(true); // Ensure scanned is set immediately
                // Stop the scanner immediately to prevent further callbacks
                setTimeout(() => {
                  forceStopScanner();
                }, 100);
              }
              return; // Do not process errors after a successful scan
            }
            // Only handle errors if scanner is still active and not already scanned
            if (
              error &&
              isWebScanActiveRef.current &&
              !scanned &&
              error.name !== 'NotFoundException' &&
              !(typeof error.message === 'string' && error.message.includes('No MultiFormat Readers were able to detect the code'))
            ) {
              console.error('ZXing scanning error:', error);
              setScanningStatus('error');
            }
          };
          
          codeReader.decodeFromVideoDevice(
            deviceIdToUse,
            videoRef.current,
            scanCallback
          );
          scannerRef.current = codeReader;
          setIsWebScannerActive(true);
          setScanningStatus('scanning');
          console.log("ZXing multi-format web scanner initialized successfully");
          return;
        } catch (zxingError) {
          console.warn('ZXing multi-format initialization failed:', zxingError);
          if (codeReader) {
            try {
              if (typeof codeReader.reset === 'function') {
                codeReader.reset();
              }
              if (typeof codeReader.stopAsyncDecode === 'function') {
                codeReader.stopAsyncDecode();
              }
            } catch (cleanupError) {
              console.error('Error cleaning up failed codeReader:', cleanupError);
            }
          }
          throw zxingError;
        }
      }
      throw new Error('No QR/barcode detection library available for web scanning');
    } catch (error) {
      console.error('Error initializing web scanner:', error);
      setWebError(error);
      setIsWebScannerActive(false);
      setScanningStatus('error');
    }
  };

  const handleBarCodeScanned = (scanningResult) => {
    if (scanned) return; // Prevent multiple scans
    let data;
    let codeType;
    if(IS_WEB){
      const { type, data: scannedData } = scanningResult;
      data = scannedData;
      codeType = type || 'unknown';
      // For web, set webData and scanned, navigation is handled in useEffect
      setWebData(data);
      setScanned(true);
      return;
    } else {
      const { type, data: scannedData, bounds: { origin } = {} } = scanningResult;
      data = scannedData;
      codeType = type;
      // For mobile, navigate directly
      setScanned(true);
      return navigation.navigate("PaymentTicket", data);
    }
  };

  const resetScanner = () => {
    setScanned(false);
    setWebError(null);
    setScanningStatus('initializing');
    setDetectedCodeType(null);
    setWebData(null);
    
    if (IS_WEB) {
      // Force stop scanner first
      forceStopScanner();
      
      // Reinitialize web scanner with delay
      if (isFocused && hasPermission) {
        // Longer delay to ensure complete cleanup
        setTimeout(() => {
          if (isFocused && hasPermission && !scanned) {
            initializeWebScanner();
          }
        }, 500);
      }
    }
  };

  // Handle window resize for responsive video sizing
  useEffect(() => {
    if (IS_WEB) {
      // Set up resize listener
      window.addEventListener('resize', handleResizeDebounced);
      
      // Initial call to set correct dimensions
      handleResizeDebounced();
      
      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResizeDebounced);
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
          resizeTimeoutRef.current = null;
        }
      };
    }
  }, [handleResizeDebounced]);

  if (hasPermission === null) {
    return <PageTitle title={"PAGAMENTO ONLINE"} navigation={navigation}>
        <View
          style={{
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            flexGrow: 1,
          }}
        >
          <Text>Pedindo permissão para acessar a camera</Text>
        </View>
    </PageTitle>
  }
  if (hasPermission === false) {
    return (
      <PageTitle title={"PAGAMENTO ONLINE"} navigation={roleUser == "condominum" || roleUser == "admin" ? navigation : null }>
        <View
          style={{
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            flexGrow: 1,
          }}
        >
          <Icon name="camera-off" size={128} />
          <Text style={{ margin: 24, fontSize: Fonts.FONT_SIZE_16 }}>
            {"Sem permissão para a camera"}
          </Text>
          <IconButton
            iconName={"camera-outline"}
            textoBotao={"Permitir"}
            estilo={{ backgroundColor: Colors.WARNING }}
            onPress={async () => {
              if (!IS_WEB) {
                BarCodeScanner.requestPermissionsAsync().then((value) => {
                  if (!value.canAskAgain) {
                    Toast.show({
                      type: 'error',
                      text1: 'Permissão negada pelo sistema',
                      text2: 'Clique aqui para ver as configurações',
                      topOffset: 90,
                      onPress: () => Linking.openSettings()
                    });
                  }
                  setHasPermission(value.granted);
                });
              } else {
                try {
                  // Request camera permission for web
                  const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                  // Stop the test stream immediately
                  stream.getTracks().forEach(track => track.stop());
                  
                  // Permission granted
                  setHasPermission(true);
                  
                  // Immediately get available cameras and auto-select first one
                  console.log("Camera permission granted via button, loading available cameras...");
                  await getAvailableCameras();
                } catch (permError) {
                  console.error("Camera permission denied:", permError);
                  Toast.show({
                    type: 'error',
                    text1: 'Permissão da câmera negada',
                    text2: 'Por favor, permita o acesso à câmera no navegador',
                    topOffset: 90,
                  });
                  setHasPermission(false);
                }
              }
            }}
          />
          <IconButton
            iconName={"qrcode-scan"}
            textoBotao={"Digitar Código"}
            estilo={{ backgroundColor: Colors.SECONDARY, marginTop: 8 }}
            onPress={() => setVisible(true)}
          />
        </View>
      </PageTitle>
    );
  }
  
  return (
    <PageTitle title={"PAGAMENTO ONLINE"} navigation={navigation}>
      <View style={{ flex: 1, width: '100%', height: '100%', alignItems: 'center', justifyContent: 'flex-start', paddingTop: 0 }}>
        {isFocused ? (
          !IS_WEB ? (
            <>
              <BarCodeScanner
                onBarCodeScanned={scanned ? undefined : handleBarCodeScanned}
                type={type}
                barCodeTypes={[
                  BarCodeScanner.Constants.BarCodeType.qr, 
                  BarCodeScanner.Constants.BarCodeType.code128,
                  BarCodeScanner.Constants.BarCodeType.code39,
                  BarCodeScanner.Constants.BarCodeType.code93,
                  BarCodeScanner.Constants.BarCodeType.interleaved2of5,
                ]}
                style={{
                  width: width / 1.25,
                  height: height / 1.50,
                }}
              />
              {scanned && (
                <View style={{ 
                  position: 'absolute', 
                  bottom: 10, 
                  left: 0, 
                  right: 0, 
                  alignItems: 'center' 
                }}>
                  <IconButton
                    iconName={"refresh"}
                    textoBotao={"Escanear Novamente"}
                    estilo={{ backgroundColor: Colors.PRIMARY }}
                    onPress={resetScanner}
                  />
                </View>
              )}
            </>
          ) : (
            <View style={{ width: '100%', height: 'calc(100vh - 56px)', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', paddingBottom: 40 }}>
              {webError ? (
                <>
                  <Text style={{ marginBottom: 20, fontSize: Fonts.FONT_SIZE_16, color: Colors.ALERT }}>
                    Erro ao acessar a câmera: {webError.message}
                  </Text>
                  <IconButton
                    iconName={"camera-refresh"}
                    textoBotao={"Tentar Novamente"}
                    estilo={{ backgroundColor: Colors.WARNING, marginBottom: 8 }}
                    onPress={() => {
                      setWebError(null);
                      setScanningStatus('initializing');
                      setTimeout(() => {
                        initializeWebScanner();
                      }, 500);
                    }}
                  />
                  <IconButton
                    iconName={"qrcode-scan"}
                    textoBotao={"Digitar Código"}
                    estilo={{ backgroundColor: Colors.SECONDARY }}
                    onPress={() => setVisible(true)}
                  />
                </>
              ) : (
                <>
                  <video 
                    ref={videoRef}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'contain',
                      display: isWebScannerActive ? 'block' : 'none',
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                  />
                  <canvas 
                    ref={canvasRef}
                    style={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'none'
                    }}
                  />
                  {!isWebScannerActive && (
                    <View style={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      right: 0, 
                      bottom: 0, 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.1)'
                    }}>
                      <Text style={{ fontSize: Fonts.FONT_SIZE_16, marginBottom: 20 }}>
                        {scanningStatus === 'initializing' ? 'Iniciando câmera...' : 
                         scanningStatus === 'error' ? 'Erro ao inicializar câmera' :
                         'Câmera não disponível'}
                      </Text>
                    </View>
                  )}
                  {isWebScannerActive && scanningStatus === 'success' && (
                    <View style={{ 
                      position: 'absolute', 
                      top: 20, 
                      left: 0, 
                      right: 0, 
                      alignItems: 'center' 
                    }}>
                      <View style={{
                        backgroundColor: 'rgba(0,128,0,0.8)',
                        paddingHorizontal: 16,
                        paddingVertical: 8,
                        borderRadius: 20
                      }}>
                        <Text style={{ 
                          fontSize: Fonts.FONT_SIZE_14, 
                          color: 'white',
                          textAlign: 'center'
                        }}>
                          Código detectado! {detectedCodeType && `(${detectedCodeType})`}
                        </Text>
                      </View>
                    </View>
                  )}
                  {isWebScannerActive && scanningStatus === 'scanning' && (
                    <View style={{ 
                      position: 'absolute', 
                      top: 20, 
                      left: 0, 
                      right: 0, 
                      alignItems: 'center' 
                    }}>
                      <View style={{
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        paddingHorizontal: 16,
                        paddingVertical: 8,
                        borderRadius: 20
                      }}>
                        <Text style={{ 
                          fontSize: Fonts.FONT_SIZE_14, 
                          color: 'white',
                          textAlign: 'center'
                        }}>
                          Posicione o QR Code ou Código de Barras na área da câmera
                        </Text>
                      </View>
                    </View>
                  )}
                  <View style={{ 
                    position: 'absolute', 
                    bottom: 42, 
                    left: 0, 
                    right: 0, 
                    alignItems: 'center' 
                  }}>
                    <IconButton
                      iconName={"qrcode-scan"}
                      textoBotao={"Digitar Código"}
                      estilo={{ backgroundColor: Colors.SECONDARY }}
                      onPress={() => setVisible(true)}
                    />
                    {scanned && (
                      <IconButton
                        iconName={"refresh"}
                        textoBotao={"Escanear Novamente"}
                        estilo={{ backgroundColor: Colors.PRIMARY, marginTop: 8 }}
                        onPress={resetScanner}
                      />
                    )}
                  </View>
                </>
              )}
              {/* Camera selection button - always show if multiple cameras available, regardless of error state */}
              {availableCameras.length > 1 && (
                <TouchableOpacity
                  style={styles.cameraSelectButton}
                  onPress={() => setShowCameraSelector(true)}
                >
                  <Icon name="camera-switch" size={24} color={Colors.WHITE} />
                </TouchableOpacity>
              )}
            </View>
          )
        ) : null}
      </View>
      <TicketKeyboard modalVisible={visible} setModalVisible={setVisible} navigation={navigation}/>
      <CameraSelectorModal />
    </PageTitle>
  );
}


const styles = StyleSheet.create({
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
  separator: {
    marginVertical: 30,
    height: 1,
    width: "80%",
  },
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalView: {
    margin: 20,
    backgroundColor: Colors.WHITE,
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    width: '80%',
    maxHeight: '70%',
  },
  closeButton: {
    marginLeft: 'auto',
    color: Colors.GRAY_DARK,
  },
  titleView: {
    marginBottom: 16,
    alignItems: 'center',
  },
  titleText: {
    textAlign: 'center',
    fontSize: Fonts.FONT_SIZE_20,
    fontWeight: 'bold',
    color: Colors.BLACK,
  },
  cameraListContainer: {
    alignItems: 'center',
    width: '100%',
  },
  cameraOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginVertical: 4,
    borderRadius: 8,
    backgroundColor: Colors.WHITE,
    borderWidth: 1,
    borderColor: Colors.GRAY_DARK,
    width: '100%',
    minWidth: 200,
  },
  selectedCameraOption: {
    backgroundColor: Colors.PRIMARY,
    borderColor: Colors.PRIMARY,
  },
  cameraOptionText: {
    marginLeft: 12,
    fontSize: Fonts.FONT_SIZE_16,
    color: Colors.BLACK,
    flex: 1,
  },
  selectedCameraOptionText: {
    color: Colors.WHITE,
    fontWeight: 'bold',
  },
  noCamerasText: {
    margin: 20,
    fontSize: Fonts.FONT_SIZE_16,
    textAlign: 'center',
    color: Colors.GRAY_DARK,
  },
  cameraSelectButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    padding: 8,
    borderRadius: 16,
    backgroundColor: Colors.PRIMARY,
  },
  videoContainer: {
    overflow: 'hidden',
    position: 'relative',
    maxWidth: '100%',
    maxHeight: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
